/**
 * DateField — drop-in replacement for `<TextField type="date">` that
 * uses MUI X DatePicker internally. Why: Chrome's native HTML5 date
 * picker reads the BROWSER UI language for its calendar UI (Clear /
 * Today buttons, month names, weekday letters), NOT `<html lang>`.
 * Result: a Chinese SVDY tenant on an English-Chrome installation
 * sees English month names. MUI X DatePicker uses an explicit
 * `adapterLocale` from `<LocalizationProvider>` so the picker UI
 * always matches the app's i18n language.
 *
 * API kept compatible with the existing TextField type="date" usages
 * so call-sites change minimally:
 *   <DateField value={state} onChange={(e) => setState(e.target.value)} />
 *
 * The onChange synthesizes a TextField-like event with
 * { target: { value: 'YYYY-MM-DD' | '', name } } so existing handlers
 * `(e) => setForm({...form, [e.target.name]: e.target.value})` work
 * unchanged.
 */

import * as React from 'react';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import type { TextFieldProps } from '@mui/material';

interface FakeEvent {
  target: { value: string; name: string };
}

export interface DateFieldProps {
  value?: string;
  onChange?: (event: FakeEvent) => void;
  onBlur?: (event: FakeEvent) => void;
  name?: string;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: TextFieldProps['variant'];
  margin?: TextFieldProps['margin'];
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  sx?: TextFieldProps['sx'];
  /** Min selectable date (ISO YYYY-MM-DD). */
  minDate?: string;
  /** Max selectable date (ISO YYYY-MM-DD). */
  maxDate?: string;
  /**
   * Optional override for the slotProps.textField — useful for
   * call-sites that needed extras like InputProps adornments.
   */
  textFieldProps?: TextFieldProps;
}

function isoToDate(s: string | undefined): Date | null {
  if (!s) return null;
  // Match YYYY-MM-DD; treat as local time noon to avoid TZ rollover.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), 12, 0, 0, 0);
}

function dateToIso(d: Date | null | undefined): string {
  if (!d || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  onBlur,
  name,
  label,
  required,
  disabled,
  fullWidth,
  size = 'small',
  variant,
  margin,
  error,
  helperText,
  placeholder,
  sx,
  minDate,
  maxDate,
  textFieldProps,
}) => {
  const dateValue = isoToDate(value);

  const handleChange: DatePickerProps<Date>['onChange'] = (date) => {
    const iso = dateToIso(date);
    onChange?.({ target: { value: iso, name: name ?? '' } });
  };

  return (
    <DatePicker
      value={dateValue}
      onChange={handleChange}
      label={label}
      disabled={disabled}
      minDate={isoToDate(minDate) ?? undefined}
      maxDate={isoToDate(maxDate) ?? undefined}
      // Render through a TextField — matches existing visual + sizing.
      slotProps={{
        textField: {
          name,
          required,
          fullWidth,
          size,
          variant,
          margin,
          error,
          helperText,
          placeholder,
          // MUI X DatePicker doesn't always stretch its inner TextField
          // to the parent the way a bare <TextField fullWidth /> does,
          // which left date fields visibly narrower than sibling inputs
          // in Grid cells. Force width:100% when fullWidth so the box
          // matches neighbouring fields exactly.
          sx: fullWidth ? { width: '100%', ...(sx as object) } : sx,
          onBlur: onBlur ? (e) => {
            // Synthesize string-target event from TextField's blur.
            onBlur({ target: { value: e.target.value, name: name ?? '' } });
          } : undefined,
          ...(textFieldProps ?? {}),
        },
      }}
    />
  );
};

export default DateField;
